/**
 * Descoberta e validação do host que embute este portal.
 *
 * O host se identifica na URL inicial do iframe (`?embed=1&host=<origin>`), e
 * a origem dele é guardada em sessionStorage: o fluxo OAuth recarrega o frame
 * (Keycloak -> /login-redirect) e leva a query string junto, mas a marca de
 * "estou embutido" e o destino das mensagens precisam sobreviver a isso.
 */

const HOST_ORIGIN_KEY = "musa_embed_host_origin";
const HOST_REALM_KEY = "musa_embed_host_realm";

/** Realms que o host pode indicar. Fora disso, ignora e usa o padrão. */
const REALMS: readonly string[] = ["generator", "musa"];
export type HostRealm = "generator" | "musa";

/**
 * Origens que podem embutir o portal.
 *
 * Confiar na origem que o próprio host declara seria confiar em quem está
 * perguntando — qualquer site abriria o portal num iframe passando
 * `?host=https://evil.example` e receberia as mensagens, menu e rota incluídos.
 * Por isso a origem declarada só vale se casar com esta lista.
 *
 * É a mesma superfície que o `frame-ancestors` do CloudFront protege; as duas
 * pontas existem porque uma sem a outra deixa metade do buraco aberto.
 */
export function isAllowedHostOrigin(origin: string): boolean {
  let url: URL;
  try {
    url = new URL(origin);
  } catch {
    return false;
  }

  const { protocol, hostname } = url;

  if (import.meta.env.DEV && (hostname === "localhost" || hostname === "127.0.0.1")) {
    return protocol === "http:" || protocol === "https:";
  }

  // Sufixo com ponto: `evilmusa.co` não casa com `.musa.co`.
  return protocol === "https:" && (hostname === "musa.co" || hostname.endsWith(".musa.co"));
}

/**
 * Está dentro de um iframe?
 *
 * Não usa a query string porque ela some no round-trip do Keycloak. A
 * comparação com `window.top` é cross-origin-safe: comparar referências de
 * janela é permitido, ler propriedades delas não.
 */
export function isEmbedded(): boolean {
  if (typeof window === "undefined") return false;
  try {
    return window.self !== window.top;
  } catch {
    // Acesso negado a window.top só acontece dentro de um frame.
    return true;
  }
}

/** Origem do host, validada. `null` = não embutido, ou host não autorizado. */
export function getHostOrigin(): string | null {
  if (!isEmbedded()) return null;

  const declared = new URLSearchParams(window.location.search).get("host");
  if (declared && isAllowedHostOrigin(declared)) {
    const origin = new URL(declared).origin;
    try {
      sessionStorage.setItem(HOST_ORIGIN_KEY, origin);
    } catch {
      // Sem storage o embed ainda funciona nesta carga; quebra depois do
      // redirect do Keycloak. Não é motivo para derrubar a tela.
    }
    return origin;
  }

  try {
    const saved = sessionStorage.getItem(HOST_ORIGIN_KEY);
    return saved && isAllowedHostOrigin(saved) ? saved : null;
  } catch {
    return null;
  }
}

/**
 * Realm em que o HOST autenticou o usuário.
 *
 * Sem isto, o `getLastRealm()` cai no padrão `generator`, porque o
 * `keycloak_last_realm` do localStorage está vazio dentro do iframe — nunca
 * houve login por aqui. Um interno logado no portal pelo realm `musa` teria a
 * reautenticação silenciosa recusada e cairia na tela de login, com a sessão
 * SSO viva no realm certo o tempo todo.
 *
 * Não é decisão de segurança: escolhe contra qual realm TENTAR. Sem sessão o
 * Keycloak recusa, e a assinatura é conferida do mesmo jeito.
 */
export function getHostRealm(): HostRealm | null {
  if (!isEmbedded()) return null;

  const declarado = new URLSearchParams(window.location.search).get("realm");
  if (declarado && REALMS.includes(declarado)) {
    try {
      sessionStorage.setItem(HOST_REALM_KEY, declarado);
    } catch {
      // Sem storage vale só nesta carga; o round-trip do Keycloak perde.
    }
    return declarado as HostRealm;
  }

  try {
    const salvo = sessionStorage.getItem(HOST_REALM_KEY);
    return salvo && REALMS.includes(salvo) ? (salvo as HostRealm) : null;
  } catch {
    return null;
  }
}

/**
 * Modo embed ligado: dentro de um iframe E com host autorizado.
 *
 * Iframe de origem desconhecida cai fora: o portal continua renderizando o
 * próprio menu, como se estivesse numa aba. Degrada para o comportamento
 * normal em vez de virar uma tela sem navegação nenhuma.
 */
export function isEmbedMode(): boolean {
  return getHostOrigin() !== null;
}

/**
 * O host pediu nova tentativa — o botão "já entrei, tentar de novo".
 *
 * A trava anti-loop (`keycloak_silent_auth_attempted`) precisa cair para uma
 * nova tentativa silenciosa acontecer, mas só sob gesto do usuário. Zerá-la a
 * cada carga do frame cria um laço: a tentativa navega o frame, o módulo
 * recarrega, a trava é zerada de novo. Já aconteceu, com 14 idas ao Keycloak
 * numa carga só.
 */
export function hostRequestedRetry(): boolean {
  if (!isEmbedded()) return false;
  return new URLSearchParams(window.location.search).has("retry");
}
